import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { getSettings, saveSettings } from "../api/actions";
import { can } from "../accessRoles";

const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
const MAX_TEXT = 256; // Setting.value is varchar(256)

const numberError = (value, { min = 0, minExclusive = false, max, message }) => {
  const n = Number(value);
  if (value === "" || value === null || value === undefined || Number.isNaN(n)) return "Enter a number.";
  if (minExclusive ? n <= min : n < min) return message;
  if (max !== undefined && n > max) return message;
  return null;
};

// One row per job a setting controls. `name` is the Setting row; rows whose setting is missing are skipped.
const GROUPS = [
  {
    title: "Late fees",
    where: "Nightly process, step 6",
    rows: [
      {
        name: "lateFees",
        type: "switch",
        label: "Charge late fees",
        help: "Adds a late fee to past-due bills during the nightly run.",
        offWarning: "Tonight's run won't add late fees to any past-due bill.",
      },
      {
        name: "lateFeeAmount",
        type: "number",
        suffix: "% of bill",
        label: "Late fee rate",
        help: "Percent of the bill total. Also printed on each bill as the amount due after the due date.",
        validate: (v) => numberError(v, { min: 0, max: 100, message: "Enter a percent from 0 to 100." }),
      },
    ],
  },
  {
    title: "New accounts",
    where: "First bill",
    rows: [
      {
        name: "setupFee",
        type: "money",
        label: "Account setup fee",
        help: "Added to an account's first bill.",
        validate: (v) => numberError(v, { min: 0, minExclusive: true, message: "The setup fee must be more than $0." }),
      },
    ],
  },
  {
    title: "Disconnects",
    where: "Nightly process, steps 4 to 7",
    rows: [
      {
        name: "disconnect",
        type: "switch",
        label: "Run disconnects",
        help: "In the nightly run: creates disconnects with 14-day notices, sends 10-day and final notices, and processes scheduled disconnects. Off stops all four.",
        offWarning: "Tonight's run won't create disconnects, send notices or process scheduled disconnects.",
      },
      {
        name: "tenDayStartDate",
        endName: "tenDayEndDate",
        type: "dateRange",
        label: "10-day letter season",
        help: "Between these dates, accounts that got a 14-day notice also get a 10-day letter before disconnect.",
      },
      {
        name: "reconnectFee",
        type: "money",
        label: "Reconnect fee",
        help: "Billed when a disconnect is processed, and printed on disconnect notices.",
        validate: (v) => numberError(v, { min: 0, minExclusive: true, message: "The reconnect fee must be more than $0." }),
      },
      {
        name: "disconnectEmail",
        type: "emails",
        label: "Reconnect alerts go to",
        help: "Gets the hourly email when a disconnected account pays down to $0.",
      },
    ],
  },
  {
    title: "Outages",
    where: "When an outage is closed",
    rows: [
      {
        name: "notificationPropertyOutage",
        type: "switch",
        label: "Email tenants when an outage ends",
        help: "Emails every active account at the property when you set the outage's end time.",
      },
    ],
  },
  {
    title: "Bills and payments",
    rows: [
      {
        name: "finalBillMessage",
        type: "text",
        label: "Final bill message",
        help: "Printed on every final bill.",
        validate: (v) => (String(v || "").trim() === "" ? "Enter a message." : null),
      },
      {
        name: "winterCrisisCoverAmount",
        type: "money",
        label: "Minimum winter crisis pledge",
        help: "Pledges below this amount are rejected.",
        validate: (v) => numberError(v, { min: 0, message: "The amount can't be negative." }),
      },
    ],
  },
  {
    title: "Features",
    rows: [
      { name: "apiKeysInternal", type: "switch", label: "API keys in Ubil+", help: "Shows the API Keys page to super admins." },
      { name: "apiKeysPortal", type: "switch", label: "API keys in the customer portal", help: "Lets portal users create their own API keys." },
    ],
  },
];

const toBool = (v) => (v === true || v === "true" || Number(v) === 1 ? "1" : "0");
const splitEmails = (v) => String(v || "").split(",").map((e) => e.trim()).filter(Boolean);

function BillingRules(props) {
  const { setting, user } = props;
  const items = setting.settings.items;
  const canEdit = can(user?.details, "billingRules");
  const byName = useMemo(() => Object.fromEntries(items.map((s) => [s.name, s])), [items]);

  // Saved values by setting name. Switches are normalized to "1"/"0".
  const saved = useMemo(() => {
    const out = {};
    GROUPS.forEach((g) => g.rows.forEach((row) => {
      [row.name, row.endName].filter(Boolean).forEach((name) => {
        const s = byName[name];
        if (s) out[name] = row.type === "switch" ? toBool(s.value) : String(s.value ?? "");
      });
    }));
    return out;
  }, [byName]);

  // Only edited settings live in the draft; everything else reads from the store.
  const [draft, setDraft] = useState({});
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(items.length === 0);

  const load = () => {
    setLoading(true);
    Promise.resolve(props.getSettings()).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (items.length === 0) load();
  }, []);

  const valueOf = (name) => (name in draft ? draft[name] : saved[name] ?? "");
  const isDirty = (name) => name in draft && draft[name] !== saved[name];
  const setValue = (name, value) => setDraft((d) => ({ ...d, [name]: value }));

  const rowError = (row) => {
    if (row.type === "dateRange") {
      const start = valueOf(row.name), end = valueOf(row.endName);
      if (!start || !end) return "Pick both dates.";
      if (end < start) return "The season has to end after it starts.";
      return null;
    }
    if (row.type === "emails") {
      const emails = splitEmails(valueOf(row.name));
      if (emails.length === 0) return "Add at least one email.";
      const bad = emails.find((e) => !EMAIL_RE.test(e));
      return bad ? `${bad} isn't a valid email.` : null;
    }
    if (row.type === "text" && String(valueOf(row.name)).length > MAX_TEXT) return `Keep it under ${MAX_TEXT} characters.`;
    return row.validate ? row.validate(valueOf(row.name)) : null;
  };

  const visibleRows = GROUPS.flatMap((g) => g.rows).filter((row) => byName[row.name]);
  const dirtyNames = Object.keys(draft).filter(isDirty);
  const dirtyRows = visibleRows.filter((row) => isDirty(row.name) || (row.endName && isDirty(row.endName)));
  const errors = Object.fromEntries(dirtyRows.map((row) => [row.name, rowError(row)]).filter(([, e]) => e));
  const hasErrors = Object.keys(errors).length > 0;

  useEffect(() => {
    if (dirtyNames.length === 0) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyNames.length]);

  const save = async () => {
    const labelFor = (name) => visibleRows.find((r) => r.name === name || r.endName === name)?.label || name;
    const changes = dirtyNames.map((name) => ({
      name,
      uuid: byName[name].uuid,
      value: name === "disconnectEmail" ? splitEmails(draft[name]).join(",") : String(draft[name]).trim(),
      label: labelFor(name),
    }));
    setSaving(true);
    const failed = await props.saveSettings(changes);
    setSaving(false);
    const failedNames = changes.filter((c) => failed.includes(c.label)).map((c) => c.name);
    setDraft((d) => Object.fromEntries(Object.entries(d).filter(([name]) => failedNames.includes(name))));
  };

  const changedLabel = (row) => {
    const dates = [row.name, row.endName].filter(Boolean).map((n) => byName[n]?.updated).filter(Boolean);
    if (dates.length === 0) return null;
    return `Changed ${moment.max(dates.map((d) => moment(d))).format("MMM D, YYYY")}`;
  };

  const renderControl = (row) => {
    const disabled = !canEdit || saving;
    const error = errors[row.name];
    switch (row.type) {
      case "switch": {
        const on = valueOf(row.name) === "1";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 28 }}>{on ? "On" : "Off"}</Typography>
            <Switch
              checked={on}
              disabled={disabled}
              color="success"
              onChange={(e) => setValue(row.name, e.target.checked ? "1" : "0")}
              slotProps={{ input: { "aria-label": row.label } }}
            />
          </Box>
        );
      }
      case "number":
      case "money":
        return (
          <TextField
            size="small"
            type="number"
            value={valueOf(row.name)}
            disabled={disabled}
            error={Boolean(error)}
            onChange={(e) => setValue(row.name, e.target.value)}
            inputProps={{ step: row.type === "money" ? "0.01" : "0.1", min: 0, "aria-label": row.label }}
            InputProps={{
              startAdornment: row.type === "money" ? <InputAdornment position="start">$</InputAdornment> : undefined,
              endAdornment: row.suffix ? <InputAdornment position="end">{row.suffix}</InputAdornment> : undefined,
            }}
            sx={{ width: row.suffix ? 180 : 140 }}
          />
        );
      case "dateRange":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <TextField size="small" type="date" value={valueOf(row.name)} disabled={disabled} error={Boolean(error)}
              onChange={(e) => setValue(row.name, e.target.value)} inputProps={{ "aria-label": "Season starts" }} />
            <Typography variant="body2" color="text.secondary">to</Typography>
            <TextField size="small" type="date" value={valueOf(row.endName)} disabled={disabled} error={Boolean(error)}
              onChange={(e) => setValue(row.endName, e.target.value)} inputProps={{ "aria-label": "Season ends" }} />
          </Box>
        );
      case "emails": {
        const emails = splitEmails(valueOf(row.name));
        const add = () => {
          const email = newEmail.trim();
          if (!email) return;
          if (!emails.some((e) => e.toLowerCase() === email.toLowerCase())) setValue(row.name, [...emails, email].join(","));
          setNewEmail("");
        };
        return (
          <Box sx={{ display: "grid", gap: 1, justifyItems: { xs: "start", md: "end" }, maxWidth: 420 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
              {emails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  color={EMAIL_RE.test(email) ? "default" : "error"}
                  onDelete={disabled ? undefined : () => setValue(row.name, emails.filter((e) => e !== email).join(","))}
                />
              ))}
            </Box>
            {canEdit && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="name@electricapl.com"
                  value={newEmail}
                  disabled={disabled}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                  inputProps={{ "aria-label": "Add an email" }}
                  sx={{ width: 230 }}
                />
                <Button size="small" variant="outlined" onClick={add} disabled={disabled || !newEmail.trim()} sx={{ textTransform: "none" }}>Add</Button>
              </Box>
            )}
          </Box>
        );
      }
      case "text":
        return (
          <TextField
            size="small"
            multiline
            minRows={2}
            value={valueOf(row.name)}
            disabled={disabled}
            error={Boolean(error)}
            onChange={(e) => setValue(row.name, e.target.value)}
            inputProps={{ maxLength: MAX_TEXT, "aria-label": row.label }}
            sx={{ width: { xs: "100%", md: 360 } }}
          />
        );
      default:
        return null;
    }
  };

  if (items.length === 0) {
    return loading ? (
      <Box sx={{ display: "grid", gap: 2 }}>
        {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={140} />)}
      </Box>
    ) : (
      <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
        Settings didn't load.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {!canEdit && <Alert severity="info">Only super admins can change these settings. You can see what each one is set to.</Alert>}

      {GROUPS.map((group) => {
        const rows = group.rows.filter((row) => byName[row.name]);
        if (rows.length === 0) return null;
        return (
          <Paper key={group.title} variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 1, px: 2.5, pt: 2, pb: 1.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.title}</Typography>
              {group.where && <Typography variant="caption" color="text.secondary">{group.where}</Typography>}
            </Box>
            {rows.map((row) => {
              const dirty = isDirty(row.name) || (row.endName && isDirty(row.endName));
              const error = errors[row.name];
              const showOffWarning = row.offWarning && dirty && valueOf(row.name) === "0";
              return (
                <Box key={row.name}>
                  <Divider />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1fr) auto" },
                      gap: 1.5,
                      alignItems: "start",
                      px: 2.5,
                      py: 1.75,
                      bgcolor: dirty ? "rgba(255, 215, 0, 0.10)" : "transparent",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{row.label}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>{row.help}</Typography>
                      <Typography variant="caption" color="text.secondary">{dirty ? "Not saved yet" : changedLabel(row)}</Typography>
                    </Box>
                    <Box sx={{ justifySelf: { xs: "start", md: "end" } }}>{renderControl(row)}</Box>
                    {error && <Alert severity="error" sx={{ gridColumn: "1 / -1", py: 0 }}>{error}</Alert>}
                    {showOffWarning && <Alert severity="warning" sx={{ gridColumn: "1 / -1", py: 0 }}>{row.offWarning}</Alert>}
                  </Box>
                </Box>
              );
            })}
          </Paper>
        );
      })}

      {dirtyNames.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: "sticky",
            bottom: 16,
            zIndex: 2,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: 2,
            bgcolor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="body2">
            {dirtyRows.length === 1 ? "1 unsaved change" : `${dirtyRows.length} unsaved changes`}
            {hasErrors && " · fix the errors above to save"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setDraft({})} disabled={saving} sx={{ color: "inherit", textTransform: "none" }}>Discard</Button>
            <Button variant="contained" color="secondary" onClick={save} disabled={saving || hasErrors} sx={{ textTransform: "none" }}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

function mapStateToProps(state) {
  return {
    setting: state.setting,
    user: state.user,
  };
}

export default connect(mapStateToProps, { getSettings, saveSettings })(BillingRules);
